import * as React from 'react';
import {Form} from 'antd';
import {FormComponentProps} from 'antd/lib/form';
import * as moment from 'moment';
import {ClientSummary, Contact, Returnal} from '../api/dtos/dtos';
import locales from '../locales';
import * as dateUtils from '../utils/date-utils';
import ClientSelector from '../client-details/ClientSelector';
import SubmitCancelFormItem from '../common/form-items/SubmitCancelFormItem';
import ClientContactSelector from '../client-details/ClientContactSelector';
import * as formUtils from '../utils/form-utils';
import FormItem from 'antd/lib/form/FormItem';
import {numberGroupFormatter} from '../utils/number-utils';
import NumberFormItem from '../common/form-items/NumberFormItem';
import DatePickerFormItem from '../common/form-items/DatePickerFormItem';
import TextAreaFormItem from '../common/form-items/TextAreaFormItem';
import StaticFormItem from '../common/form-items/StaticFormItem';
import {EditorMode} from '../common/types';
import accountProvider from "../account/AccountProvider";


interface ReturnalFormProps extends FormComponentProps {
  mode: EditorMode;
  editable: boolean;
  returnal?: Returnal;
  onReturnalChange: (returnal: Returnal) => void;
  onFormSubmit: () => void;
  onFormCancel: () => void;
}

const FIELD_CREATED = 'date';
const FIELD_TRANSPORT_COST = 'transportCost';
const FIELD_COMMENT = 'comment';
const FIELD_CAUTION = 'caution';

class ReturnalForm extends React.Component<ReturnalFormProps, {}> {

  public render() {
    const {editable, form, returnal, mode} = this.props;
    const disabled = !editable;
    const created = (returnal && returnal.created) ? dateUtils.toMomentOrThrow(returnal.created) : moment();
    const dateEditorDisabled = disabled || mode === 'edit';
    const isAuthorised = accountProvider.isAdmin() || accountProvider.isSales();
    return (
      <>
        <Form>
          <StaticFormItem
            label={locales.id}
            initialValue={returnal && <b>{returnal.idString}</b>}
          />

          <DatePickerFormItem
            form={form}
            fieldId={FIELD_CREATED}
            label={locales.return_date}
            initialValue={created}
            disabled={dateEditorDisabled}
            required={true}
            showTime={true}
            disabledDate={this.disabledCreateDate}
          />

          <FormItem {...formUtils.formItemLayout} label={locales.rental_client}>
            <ClientSelector
              editable={this.isCreateMode()}
              client={this.getClient()}
              onSelect={this.onClientSelect}/>
          </FormItem>

          <FormItem {...formUtils.formItemLayout} label={locales.client_responsible}>
            <ClientContactSelector
              disabled={disabled}
              contact={returnal && returnal.responsible}
              client={returnal && returnal.client}
              onContactChange={this.onResponsibleChange}
            />
          </FormItem>

          {returnal?.createdBy &&
              <StaticFormItem
                  label={locales.created_by}
                  initialValue={returnal.createdBy}
              />
          }
          {returnal?.lastChangedBy &&
              <StaticFormItem
                  label={locales.last_changed_by}
                  initialValue={returnal.lastChangedBy}
              />
          }
          {returnal?.lastChangedAt &&
              <StaticFormItem
                  label={locales.last_changed_at}
                  initialValue={dateUtils.formatDateTimeOrNull(returnal.lastChangedAt)}
              />
          }


          <TextAreaFormItem
            label={locales.comment}
            form={form}
            fieldId={FIELD_COMMENT}
            initialValue={returnal && returnal.comment}
            required={false}
            disabled={true}
            maxLength={255}
          />

          <NumberFormItem
            label={locales.transport_cost}
            fieldId={FIELD_TRANSPORT_COST}
            disabled={disabled}
            form={form}
            initialValue={returnal && returnal.transportCost}
            required={false}
            min={0}
            precision={0}
            formatter={numberGroupFormatter}
          >
            <> {locales.app_currency}</>
          </NumberFormItem>
        </Form>

        {isAuthorised && this.props.children}

        {isAuthorised && <SubmitCancelFormItem
            onSubmit={this.onFormSubmit}
            onCancel={this.onFormCancel}
            disabled={disabled}
        />}
      </>
    );
  }

  private disabledCreateDate = (current: moment.Moment | null): boolean => {
    if (!current) {
      return false;
    }
    return current.isAfter(moment());
  };

  private isCreateMode = (): boolean => this.props.mode === 'create';

  private getClient = (): ClientSummary | undefined => {
    return this.props.returnal ? this.props.returnal.client : undefined;
  };

  private onClientSelect = (client?: ClientSummary) => {
    // The client is changed => clear the RentalItems grid
    const {returnal, onReturnalChange} = this.props;
    onReturnalChange({
      ...returnal,
      rentalItems: [],
      client,
    });
  };

  private onResponsibleChange = (responsible?: Contact) => {
    const {returnal, onReturnalChange} = this.props;
    onReturnalChange({
      ...returnal,
      responsible,
    });
  };

  private onFormCancel = () => {
    this.props.form.resetFields();
    this.props.onFormCancel();
  };

  private onFormSubmit = () => {
    const {returnal, onReturnalChange, form, onFormSubmit} = this.props;
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        onReturnalChange({
          ...returnal,
          created: values[FIELD_CREATED],
          comment: values[FIELD_COMMENT],
          transportCost: values[FIELD_TRANSPORT_COST],
          caution: values[FIELD_CAUTION],
        });
        onFormSubmit();
      }
    });
  };

}

export default Form.create<ReturnalFormProps>()(ReturnalForm);



// WEBPACK FOOTER //
// ./src/return-details/ReturnalForm.tsx